import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Tooltip, Circle, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useClosestStops } from '@/lib/queries'
import type { NearbyStop } from '@/lib/api'
import { DataError } from './DataError'

type Language = 'el' | 'en'

export interface FavoriteStopPin {
  stopCode: string
  stopName: string
  lat: number
  lng: number
  lineId: string
  routeCode: string
}

interface NearbyMapProps {
  lang: Language
  translations: {
    loading: string
    locationError: string
    enableLocation: string
    nearbyStops: string
    metersAway: string
    noStopsNearby: string
  }
  favoriteStops?: FavoriteStopPin[]
}

const userIcon = new L.DivIcon({
  className: 'map-marker user-marker',
  html: '<div class="marker-inner user-location"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10]
})

const stopIcon = new L.DivIcon({
  className: 'map-marker stop-marker',
  html: '<div class="marker-inner"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8]
})

const favoriteStopIcon = new L.DivIcon({
  className: 'map-marker favorite-stop-marker',
  html: '<div class="marker-inner favorite"><span class="star">★</span></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12]
})

function FitBoundsToStops({ userLat, userLng, stops }: { userLat: number; userLng: number; stops: NearbyStop[] }) {
  const map = useMap()

  useEffect(() => {
    const points: [number, number][] = [[userLat, userLng]]
    
    stops.slice(0, 10).forEach(stop => {
      points.push([parseFloat(stop.StopLat), parseFloat(stop.StopLng)])
    })

    if (points.length > 1) {
      const bounds = L.latLngBounds(points)
      map.fitBounds(bounds, { padding: [50, 50] })
    } else {
      map.setView([userLat, userLng], 16)
    }
  }, [userLat, userLng, stops, map])

  return null
}

export function NearbyMap({ lang, translations: t, favoriteStops = [] }: NearbyMapProps) {
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false)
  const { location, error: geoError, isLoading: geoLoading, requestLocation } = useGeolocation(false)
  
  const { data: nearbyStops, isLoading: stopsLoading, isError: stopsError, error: stopsQueryError, refetch: refetchStops } = useClosestStops(
    location?.lat,
    location?.lng
  )

  const getStopName = (stop: NearbyStop) =>
    lang === 'en' ? (stop.StopDescrEng || stop.StopDescr) : stop.StopDescr

  const sortedStops = useMemo(() => {
    if (!nearbyStops) return []
    return [...nearbyStops].sort((a, b) => 
      parseFloat(a.distance) - parseFloat(b.distance)
    )
  }, [nearbyStops])

  const handleEnableLocation = () => {
    setHasRequestedLocation(true)
    requestLocation()
  }

  // Auto-set hasRequestedLocation if we already have location on mount
  useEffect(() => {
    if (location && !hasRequestedLocation) {
      setHasRequestedLocation(true)
    }
  }, [location, hasRequestedLocation])

  // Not yet requested location and no auto location available
  if (!hasRequestedLocation && !location && !geoLoading) {
    return (
      <div className="nearby-map-prompt">
        <div className="location-prompt-content">
          <div className="location-icon">📍</div>
          <p>{t.enableLocation}</p>
          <button 
            className="enable-location-btn"
            onClick={handleEnableLocation}
          >
            {lang === 'en' ? 'Enable Location' : 'Ενεργοποίηση Τοποθεσίας'}
          </button>
        </div>
      </div>
    )
  }

  // Loading location
  if (geoLoading) {
    return (
      <div className="nearby-map-loading">
        <div className="loading">{t.loading}</div>
      </div>
    )
  }

  // Location error
  if (geoError || !location) {
    return (
      <div className="nearby-map-error">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <p>{t.locationError}</p>
          <p className="error-detail">{geoError}</p>
          <button 
            className="retry-btn"
            onClick={requestLocation}
          >
            {lang === 'en' ? 'Retry' : 'Επανάληψη'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="nearby-map-container">
      <div className="map-view-container nearby-view">
        <MapContainer
          center={[location.lat, location.lng]}
          zoom={16}
          className="map-view"
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitBoundsToStops 
            userLat={location.lat} 
            userLng={location.lng} 
            stops={sortedStops} 
          />

          {/* User location with accuracy circle */}
          <Circle
            center={[location.lat, location.lng]}
            radius={50}
            pathOptions={{
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 0.1,
              weight: 2
            }}
          />
          
          <Marker
            position={[location.lat, location.lng]}
            icon={userIcon}
          >
            <Tooltip direction="top" offset={[0, -10]} className="map-tooltip">
              {lang === 'en' ? 'You are here' : 'Είστε εδώ'}
            </Tooltip>
          </Marker>

          {/* Nearby stops (exclude favorites to avoid duplicates - check both stopCode and coordinates) */}
          {sortedStops.slice(0, 15)
            .filter(stop => !favoriteStops.some(fav => 
              fav.stopCode === stop.StopCode || 
              (Math.abs(fav.lat - parseFloat(stop.StopLat)) < 0.0001 && 
               Math.abs(fav.lng - parseFloat(stop.StopLng)) < 0.0001)
            ))
            .map(stop => (
              <Marker
                key={stop.StopCode}
                position={[parseFloat(stop.StopLat), parseFloat(stop.StopLng)]}
                icon={stopIcon}
                eventHandlers={{
                  click: (e) => {
                    const url = `/stop/${stop.StopCode}`
                    
                    // Check for modifier keys to determine opening behavior
                    if (e.originalEvent.ctrlKey || e.originalEvent.metaKey || e.originalEvent.button === 1) {
                      // Ctrl/Cmd+click or middle-click = open in new tab
                      window.open(url, '_blank', 'noopener,noreferrer')
                    } else {
                      // Normal click = navigate in same tab
                      window.location.href = url
                    }
                  }
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} className="map-tooltip">
                  <div className="tooltip-content">
                    <span className="tooltip-name">{getStopName(stop)}</span>
                    <span className="tooltip-distance">{Math.round(parseFloat(stop.distance))}m</span>
                    <div className="tooltip-link">{lang === 'en' ? 'Click for details' : 'Κάντε κλικ για λεπτομέρειες'}</div>
                  </div>
                </Tooltip>
              </Marker>
            ))}

          {/* Favorite stops (starred pins) */}
          {favoriteStops.map(fav => (
            <Marker
              key={`fav-${fav.stopCode}-${fav.routeCode}`}
              position={[fav.lat, fav.lng]}
              icon={favoriteStopIcon}
              eventHandlers={{
                click: (e) => {
                  const url = `/${fav.lineId.toLowerCase()}/${fav.routeCode}/${fav.stopCode}`
                  
                  if (e.originalEvent.ctrlKey || e.originalEvent.metaKey || e.originalEvent.button === 1) {
                    window.open(url, '_blank', 'noopener,noreferrer')
                  } else {
                    window.location.href = url
                  }
                }
              }}
            >
              <Tooltip direction="top" offset={[0, -12]} className="map-tooltip favorite-tooltip">
                <div className="tooltip-content">
                  <span className="tooltip-favorite-badge">★ {lang === 'en' ? 'Favorite' : 'Αγαπημένο'}</span>
                  <span className="tooltip-name">{fav.stopName}</span>
                  <span className="tooltip-line">{lang === 'en' ? 'Line' : 'Γραμμή'} {fav.lineId}</span>
                  <div className="tooltip-link">{lang === 'en' ? 'Click for ETA' : 'Κάντε κλικ για ETA'}</div>
                </div>
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Stops list below map */}
      <div className="nearby-stops-list">
        <h3>{t.nearbyStops}</h3>
        {stopsLoading ? (
          <div className="loading">{t.loading}</div>
        ) : stopsError ? (
          <DataError error={stopsQueryError} lang={lang} onRetry={() => void refetchStops()} />
        ) : sortedStops.length === 0 ? (
          <div className="no-stops">{t.noStopsNearby}</div>
        ) : (
          <div className="stops-list">
            {sortedStops.slice(0, 10).map(stop => (
              <Link
                key={stop.StopCode}
                to={`/stop/${stop.StopCode}`}
                className="nearby-stop-item"
              >
                <div className="stop-info">
                  <div className="stop-name">{getStopName(stop)}</div>
                  {stop.StopStreet && (
                    <div className="stop-street">
                      {lang === 'en' ? (stop.StopStreetEng || stop.StopStreet) : stop.StopStreet}
                    </div>
                  )}
                </div>
                <div className="stop-distance">
                  {Math.round(parseFloat(stop.distance))}{t.metersAway}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
